import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  getAllVersions, 
  getVersionByNumber, 
  compareVersions 
} from '../../services/versionService';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import './AdminPages.css';

const AdminVersions = () => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion1, setSelectedVersion1] = useState(null);
  const [selectedVersion2, setSelectedVersion2] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [loadingComparison, setLoadingComparison] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const data = await getAllVersions();
      setVersions(data);
    } catch (error) {
      console.error('Error fetching versions:', error);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCompareVersions = async () => {
    if (!selectedVersion1 || !selectedVersion2) {
      alert('Please select two versions to compare');
      return;
    }

    if (selectedVersion1 === selectedVersion2) {
      alert('Please select two different versions');
      return;
    }

    try {
      setLoadingComparison(true);
      const result = await compareVersions(selectedVersion1, selectedVersion2);
      setComparison(result);
      setShowComparison(true);
    } catch (error) {
      console.error('Error comparing versions:', error);
      alert('Failed to compare versions');
    } finally {
      setLoadingComparison(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const renderVersionCard = (version) => (
    <motion.div
      key={version.id}
      className="version-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="version-header">
        <h3>Version {version.version}</h3>
        <span className="version-date">{formatDate(version.publishedAt)}</span>
      </div>
      
      <div className="version-details">
        <p><strong>Published by:</strong> {version.publishedBy || 'Unknown'}</p>
        <p><strong>Total Questions:</strong> {version.questionsSnapshot?.length || 0}</p>
        <p><strong>Change Summary:</strong> {version.changesSummary || 'No summary provided'}</p>
      </div>

      <div className="version-pillar-scores">
        <h4>Pillar Max Scores:</h4>
        <div className="pillar-scores-grid">
          {version.pillarMaxScores && Object.entries(version.pillarMaxScores).map(([pillar, score]) => (
            <div key={pillar} className="pillar-score-item">
              <span className="pillar-name">{pillar}:</span>
              <span className="pillar-score">{score}</span>
            </div>
          ))}
        </div>
        <p><strong>Total Max Score:</strong> {version.totalMaxScore || 0}</p>
      </div>

      <div className="version-actions">
        <Button
          variant={selectedVersion1 === version.version ? 'primary' : 'secondary'}
          onClick={() => setSelectedVersion1(version.version)}
        >
          {selectedVersion1 === version.version ? 'Selected for Compare 1' : 'Select for Compare 1'}
        </Button>
        <Button
          variant={selectedVersion2 === version.version ? 'primary' : 'secondary'}
          onClick={() => setSelectedVersion2(version.version)}
        >
          {selectedVersion2 === version.version ? 'Selected for Compare 2' : 'Select for Compare 2'}
        </Button>
      </div>
    </motion.div>
  );

  const renderComparison = () => {
    if (!comparison) return null;

    const { summary, details } = comparison;

    return (
      <motion.div
        className="comparison-panel"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="comparison-header">
          <h2>Version Comparison</h2>
          <Button variant="secondary" onClick={() => setShowComparison(false)}>
            Close
          </Button>
        </div>

        <div className="comparison-summary">
          <p>Comparing Version {selectedVersion1} with Version {selectedVersion2}</p>
          <div className="summary-stats">
            <span>Added: {summary.added}</span>
            <span>Removed: {summary.removed}</span>
            <span>Modified: {summary.modified}</span>
            <span>Unchanged: {summary.unchanged}</span>
          </div>
        </div>

        {details.added && details.added.length > 0 && (
          <div className="comparison-section added">
            <h3>Added Questions ({details.added.length})</h3>
            <div className="question-list">
              {details.added.map((q, idx) => (
                <div key={idx} className="question-item">
                  <span className="question-key">{q.key}</span>
                  <span className="question-text">{q.text?.en || 'No text'}</span>
                  <span className="question-pillar">{q.pillar}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {details.removed && details.removed.length > 0 && (
          <div className="comparison-section removed">
            <h3>Removed Questions ({details.removed.length})</h3>
            <div className="question-list">
              {details.removed.map((q, idx) => (
                <div key={idx} className="question-item">
                  <span className="question-key">{q.key}</span>
                  <span className="question-text">{q.text?.en || 'No text'}</span>
                  <span className="question-pillar">{q.pillar}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {details.modified && details.modified.length > 0 && (
          <div className="comparison-section modified">
            <h3>Modified Questions ({details.modified.length})</h3>
            <div className="question-list">
              {details.modified.map((change, idx) => (
                <div key={idx} className="question-item modified-item">
                  <div className="question-header">
                    <span className="question-key">{change.key}</span>
                  </div>
                  <div className="changes-list">
                    {Object.entries(change.changes).map(([field, values]) => (
                      <div key={field} className="change-detail">
                        <strong>{field}:</strong>
                        <div className="change-values">
                          <span className="old-value">Before: {JSON.stringify(values.before)}</span>
                          <span className="new-value">After: {JSON.stringify(values.after)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {summary.added === 0 && summary.removed === 0 && summary.modified === 0 && (
          <div className="no-changes">
            <p>No differences found between these versions.</p>
          </div>
        )}
      </motion.div>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="admin-versions">
      <div className="admin-header">
        <h1>Version History</h1>
        <p>View and compare published question snapshots</p>
      </div>

      {versions.length === 0 ? (
        <div className="no-versions">
          <p>No published versions yet. Publish your first snapshot from the Questions page.</p>
        </div>
      ) : (
        <>
          <div className="comparison-controls">
            <div className="selected-versions">
              <div className="selected-version">
                <label>Version 1:</label>
                <span>{selectedVersion1 || 'Not selected'}</span>
              </div>
              <div className="selected-version">
                <label>Version 2:</label>
                <span>{selectedVersion2 || 'Not selected'}</span>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleCompareVersions}
              disabled={!selectedVersion1 || !selectedVersion2 || loadingComparison}
            >
              {loadingComparison ? 'Comparing...' : 'Compare Versions'}
            </Button>
          </div>

          {showComparison && renderComparison()}

          <div className="versions-list">
            {versions.map(version => renderVersionCard(version))}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminVersions;
